import { memo, type Ref } from 'react'
import { clsx } from 'clsx'

import css from './CommandPalette.module.scss'

import Modal from '@/components/Modal'

import type { CommandPaletteRoute } from '@/constants/appRoutes'

import type { ControllerProps } from './CommandPalette.controller'

export interface RouteOption extends CommandPaletteRoute {
  isCurrentRoute: boolean
}

export interface ViewProps extends ControllerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  query: string
  onQueryChange: (query: string) => void
  routes: RouteOption[]
  selectedIndex: number
  selectedRouteRef?: Ref<HTMLButtonElement>
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  onSelectRoute: (route: CommandPaletteRoute) => void
}

const View = memo(function CommandPaletteView({
  className,
  isOpen,
  onOpenChange,
  query,
  onQueryChange,
  routes,
  selectedIndex,
  selectedRouteRef,
  onKeyDown,
  onSelectRoute
}: ViewProps) {
  let previousGroup: RouteOption['group'] | undefined

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} ariaLabel="Route switcher" size="lg" className={css.modal}>
      <div className={clsx('CommandPalette', css.root, className)} onKeyDown={onKeyDown}>
        <input
          autoFocus
          id="command-palette-search"
          className={css.searchInput}
          type="search"
          placeholder="Search routes…"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          aria-label="Search routes"
          aria-controls="command-palette-routes"
        />

        {routes.length ? (
          <ul id="command-palette-routes" className={css.routeList} aria-label="Available routes">
            {routes.map((route, index) => {
              const startsNewGroup =
                previousGroup !== route.group && route.group !== 'Home' && route.group !== 'Workspace'
              previousGroup = route.group
              const Icon = route.icon

              return (
                <li key={route.to} className={css.routeListItem}>
                  {startsNewGroup && <span className={css.groupLabel}>{route.group}</span>}
                  <button
                    id={`command-palette-route-${index}`}
                    type="button"
                    ref={index === selectedIndex ? selectedRouteRef : undefined}
                    className={clsx(css.routeButton, {
                      [css.selected ?? '']: index === selectedIndex,
                      [css.currentRoute ?? '']: route.isCurrentRoute
                    })}
                    onClick={() => onSelectRoute(route)}
                    aria-current={route.isCurrentRoute ? 'page' : undefined}
                  >
                    <Icon className={css.routeIcon} aria-hidden="true" />
                    <span className={css.routeText}>
                      <span className={css.routeLabel}>{route.label}</span>
                      <span className={css.routePath}>{route.to}</span>
                    </span>
                    {route.isCurrentRoute && <span className={css.currentBadge}>Current</span>}
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          <div className={css.emptyState} role="status" aria-label="No matching routes" />
        )}
      </div>
    </Modal>
  )
})

View.displayName = 'CommandPalette_View'

export default View
